import type React from 'react';
import { type ComponentType, type FC, type RefObject, useState } from 'react';

import classNames from 'classnames';

import type { MetadataValues } from '@iconik-io/iconik-api/src/schema_patched/metadata';
import {
  OBJECT_TYPE_ASSETS,
  OBJECT_TYPE_COLLECTIONS,
  OBJECT_TYPE_SEGMENTS,
} from '@iconik-io/iconik-api/src/types/object_types';

import { DEFAULT_CATEGORY_NAME } from '../../constants/metadata';
import VARIANTS from '../../constants/variants';
import {
  ASSET_CATEGORY_METADATA_FIELD,
  COLLECTION_CATEGORY_METADATA_FIELD,
  SEGMENT_CATEGORY_METADATA_FIELD,
} from '../../ducks/metadata_values/thunks';
import type {
  CategoryObjectType,
  MetadataCategoryObjectType,
} from '../../ducks/pages/admin_metadata_categories/types';
import { objectTypeCategoriesSupport } from '../../ducks/pages/admin_metadata_categories/utils';
import type { AssetType } from '../../ducks/pages/asset/types';
import Icon, { ICONS } from '../../icons/icon';
import Alert from '../../ui/alert/alert';
import Button from '../../ui/button/button';
import CheckboxBasic from '../../ui/checkbox_basic/checkbox_basic';
import SelectMetadataCategoryFieldWrapper from '../../ui/forms/field_wrappers/select_metadata_category/select_metadata_category';
import SelectMetadataViewFieldWrapper from '../../ui/forms/field_wrappers/select_metadata_view/select_metadata_view';
import { getSuffix, removeSuffix } from '../../ui/forms/utils/utils';
import SendHint from '../../ui/send_hint/send_hint';
import MetadataFormContainer from '../metadata_form/metadata_form_container';
import metadataFieldRenderer, {
  type MetadataFieldRenderer,
} from '../metadata_form_field/metadata_form_field';
import type { MetadataFieldSearchRenderer } from '../metadata_form_field/search';

import styles from './entity_metadata.module.css';

type CategoryLabelProps = {
  withCheckbox: boolean;
  value?: boolean;
  htmlFor?: string;
  onChange?: (value: boolean) => void;
};

export function CategoryLabel(props: CategoryLabelProps) {
  return (
    <div className={styles.category_label_root}>
      {props.withCheckbox ? (
        <CheckboxBasic
          value={props.value ? 'checked' : null}
          onChange={(value) => {
            props?.onChange?.(value === 'checked');
          }}
        />
      ) : null}
      <label htmlFor={props.htmlFor}>Category</label>
    </div>
  );
}

const setMetadataCategoryField = (
  objectType: CategoryObjectType | undefined,
  categoryName: string,
  data: MetadataValues,
): MetadataValues => {
  let result = data;
  switch (objectType) {
    case OBJECT_TYPE_SEGMENTS:
      result = {
        ...result,
        [SEGMENT_CATEGORY_METADATA_FIELD]: categoryName,
      };
      break;
    case OBJECT_TYPE_ASSETS:
      result = {
        ...result,
        [ASSET_CATEGORY_METADATA_FIELD]: categoryName,
      };
      break;
    case OBJECT_TYPE_COLLECTIONS:
      result = {
        ...result,
        [COLLECTION_CATEGORY_METADATA_FIELD]: categoryName,
      };
      break;
    default:
      break;
  }

  return result;
};

function DefaultButtons({
  disabled,
  buttonsDisabled,
  isSaveDisabled,
  loading = true,
  submitByCommand,
  onCancelEdit,
  updateLabel = 'Update',
  cancelLabel = 'Cancel',
}: EntityMetadataProps) {
  if (disabled || loading) {
    return null;
  }
  return (
    <div className={styles.buttons}>
      <Button
        disabled={buttonsDisabled}
        variant={VARIANTS.WARNING}
        onClick={onCancelEdit}
      >
        {cancelLabel}
      </Button>
      <div className={styles.confirm_group}>
        {submitByCommand && !isSaveDisabled && <SendHint />}
        <Button
          disabled={buttonsDisabled || isSaveDisabled}
          variant={VARIANTS.SUCCESS}
          uiKey="modal-confirm-button"
          type="submit"
        >
          {updateLabel}
        </Button>
      </div>
    </div>
  );
}

export type EntityMetadataProps = {
  breakpoint?: number;
  buttonsComponent?: ComponentType<EntityMetadataProps>;
  cancelLabel?: string;
  collection?: boolean;
  componentClass?: string;
  disabled?: boolean;
  buttonsDisabled?: boolean;
  entityName?: string;
  initialValuesSetter?: <V>(value: V) => boolean;
  loading: boolean;

  showCategoriesSelector?: boolean;
  categoryName?: string;
  onCategoryChange?: (
    categoryName: string,
    sideEffect: boolean,
    categoryObjectType: MetadataCategoryObjectType,
  ) => void;
  itemCategory?: string;

  showViewsSelector?: boolean;
  viewId?: string;
  onViewChange?: (viewId: string) => void;
  metadataViewIds?: string[];

  fieldRenderer?: MetadataFieldRenderer | MetadataFieldSearchRenderer;
  metadataValues: MetadataValues;

  notificationUpdate?: boolean;
  objectType?: CategoryObjectType;
  onCancelEdit?: (event: React.MouseEvent) => void;
  onEdit?: (event: React.MouseEvent<HTMLButtonElement>) => void;
  onFormChange?: (data: MetadataValues) => void;
  onLayoutChanged?: () => void;
  onFormClick?: (event: React.MouseEvent<HTMLElement>) => void;
  onRefresh?: () => void;
  params?: Record<string, string | undefined>;
  submitMetadata?: (values: MetadataValues) => void;
  updateLabel?: string;
  visibleMetadata: boolean;
  submitByCommand?: boolean;
  hideCategorySelect?: boolean;
  bulkUpdateMode?: boolean;
  onModifierValueChange?: (key: string, value: unknown) => void;
  formRef?: RefObject<HTMLFormElement | null>;
  onInvalidSubmit?: () => void;
  prefix?: string;
  assetType?: AssetType;
  itemId?: string;
  selectedCategoryObjectType?: MetadataCategoryObjectType;
  isSaveDisabled?: boolean;
  updateOnSideEffect?: boolean;
  aiMetadataButtonComponent?: ComponentType<{ itemId: string }>;
};

const EntityMetadata: FC<EntityMetadataProps> = (props) => {
  const [viewsLength, setViewsLength] = useState(0);
  const [categoriesLength, setCategoriesLength] = useState(0);
  const [isCategoryBulkUpdate, setIsCategoryBulkUpdate] = useState(false);

  const {
    fieldRenderer = metadataFieldRenderer,
    visibleMetadata,
    viewId,
    showViewsSelector = true,
    onViewChange,
    onCategoryChange,
    showCategoriesSelector = true,
    initialValuesSetter,
    objectType,
    buttonsComponent: ButtonsComponent = DefaultButtons,
    notificationUpdate,
    metadataValues,
    onRefresh,
    componentClass,
    submitMetadata,
    metadataViewIds,
    disabled,
    onFormClick,
    loading = true,
    onFormChange,
    onLayoutChanged,
    categoryName = DEFAULT_CATEGORY_NAME,
    itemCategory,
    bulkUpdateMode,
    onModifierValueChange,
    formRef,
    onInvalidSubmit,
    prefix,
    assetType,
    itemId,
    updateOnSideEffect,
    selectedCategoryObjectType = objectType,
    aiMetadataButtonComponent: AiMetadataButtonComponent,
  } = props;

  if (!visibleMetadata) {
    return null;
  }

  const withCategorySelect =
    objectType &&
    showCategoriesSelector &&
    objectTypeCategoriesSupport.has(objectType);

  const withViewSelect = objectType && showViewsSelector;

  const withAdditionalSegmentsCategories =
    assetType === 'SUBCLIP' && objectType !== OBJECT_TYPE_SEGMENTS;

  return (
    <>
      <div
        className={classNames({
          [styles.bottom_divider]: categoriesLength > 1 || viewsLength > 1,
        })}
      >
        {withCategorySelect && objectType ? (
          <div
            className={classNames({
              [styles.hidden]: props.hideCategorySelect,
            })}
          >
            <SelectMetadataCategoryFieldWrapper
              hiddenWhenSingle
              labelComponent={({ htmlFor }: { htmlFor?: string }) => (
                <CategoryLabel
                  onChange={(value) => {
                    setIsCategoryBulkUpdate(value);
                  }}
                  value={isCategoryBulkUpdate}
                  htmlFor={htmlFor}
                  withCheckbox={bulkUpdateMode ?? false}
                />
              )}
              selectFirstAvailableOption
              selectFirstWhenValueMissing
              objectType={objectType}
              withAdditionalSegmentsCategories={
                withAdditionalSegmentsCategories
              }
              value={categoryName}
              onLoaded={(data) => {
                setCategoriesLength(data.length);
                setTimeout(() => onLayoutChanged?.(), 0);
              }}
              onChange={(value, event) => {
                const categoryObjectType =
                  getSuffix(value) === 'segments'
                    ? OBJECT_TYPE_SEGMENTS
                    : (objectType as MetadataCategoryObjectType);
                const changedValue = removeSuffix(value);
                if (!changedValue || changedValue === categoryName) {
                  return;
                }

                // event exists when change is initiated by user
                onCategoryChange?.(changedValue, !event, categoryObjectType);
                setTimeout(() => onLayoutChanged?.(), 0);
              }}
              savedValue={itemCategory}
              vertical
            />
          </div>
        ) : null}
        {withViewSelect && selectedCategoryObjectType ? (
          <SelectMetadataViewFieldWrapper
            onLoaded={(data) => {
              setViewsLength(data.length);
              setTimeout(() => onLayoutChanged?.(), 0);
            }}
            hiddenWhenSingle
            key={`${selectedCategoryObjectType}-${categoryName}`}
            label="Metadata View"
            selectFirstAvailableOption
            isMulti={false}
            objectType={selectedCategoryObjectType}
            category={categoryName}
            value={viewId}
            onChange={(value) => {
              if (!value) {
                return;
              }
              onViewChange?.(value);
              setTimeout(() => onLayoutChanged?.(), 0);
            }}
            metadataViewIds={metadataViewIds}
            vertical
          />
        ) : null}
      </div>
      {objectType === OBJECT_TYPE_ASSETS &&
      itemId &&
      AiMetadataButtonComponent ? (
        <AiMetadataButtonComponent itemId={itemId} />
      ) : null}
      {notificationUpdate ? (
        <Alert
          className={styles.alert}
          actionIcon={ICONS.Refresh}
          onActionClick={onRefresh}
        >
          Values have been updated.
        </Alert>
      ) : null}
      {bulkUpdateMode && (
        <div className={styles.bulk_update_hint}>
          <Icon textAlign icon={ICONS.Info} />
          &nbsp;Only marked fields will be updated
        </div>
      )}
      <MetadataFormContainer
        onFormClick={onFormClick}
        componentClass={componentClass}
        onFormChange={onFormChange}
        onFormSubmit={(formData) => {
          let data: MetadataValues = JSON.parse(
            JSON.stringify(formData, (key, value) => {
              // TODO: we should avoid submitting warningLabel
              // https://linear.app/iconik/issue/FE-2561/refactor-usage-of-warninglabel-in-dropdown
              if (key === 'warningLabel') {
                return;
              }
              return value ?? null;
            }),
          );

          // Add metadata category field
          data = setMetadataCategoryField(
            selectedCategoryObjectType,
            categoryName,
            data,
          );

          if (isCategoryBulkUpdate) {
            data = {
              ...data,
              __iconik_field_update_bulk____asset_category: 'checked',
            };
          }
          submitMetadata?.(data);
        }}
        metadataValues={metadataValues}
        disabled={disabled}
        loading={loading}
        metadataViewId={viewId}
        fieldRenderer={fieldRenderer}
        onLayoutChanged={onLayoutChanged}
        initialValuesSetter={initialValuesSetter}
        vertical
        objectType={selectedCategoryObjectType}
        onModifierValueChange={onModifierValueChange}
        formRef={formRef}
        onInvalidSubmit={onInvalidSubmit}
        prefix={prefix}
        updateOnSideEffect={updateOnSideEffect}
        isSaveDisabled={props.isSaveDisabled}
      >
        <ButtonsComponent {...props} />
      </MetadataFormContainer>
    </>
  );
};

export default EntityMetadata;
